import { useMemo } from 'react';
import { useCurrentInventoryPackScope } from './queries';
import {
  buildCurrentInventoryPackScopeTitle,
  getCurrentInventoryPackScopeKeySet,
  hasRequiredCurrentInventoryPackScopeParams,
  parseCurrentInventoryPackScopeQuery,
} from '../utils';

type UseCurrentInventoryPackScopeViewResult = {
  packScopeQueryParams: ReturnType<typeof parseCurrentInventoryPackScopeQuery>;
  activePackScope: { packs: Array<{ game_id: string; pack_id: string }> } | null;
  packScopeKeys: Set<string> | null;
  isPackScopedView: boolean;
  isPackScopeResolving: boolean;
  shouldShowPackScopeMissingBanner: boolean;
  pageTitle: string;
};

export const useCurrentInventoryPackScopeView = (
  storeId: string | undefined,
  searchString: string
): UseCurrentInventoryPackScopeViewResult => {
  const packScopeQueryParams = useMemo(
    () => parseCurrentInventoryPackScopeQuery(new URLSearchParams(searchString)),
    [searchString]
  );

  const hasPackScopeQuery = Boolean(packScopeQueryParams);
  const hasResolvablePackScopeQuery = Boolean(
    packScopeQueryParams && hasRequiredCurrentInventoryPackScopeParams(packScopeQueryParams)
  );

  const {
    packScope: resolvedPackScope,
    isLoading: isPackScopeLoading,
    isError: isPackScopeError,
  } = useCurrentInventoryPackScope(storeId, packScopeQueryParams, {
    enabled: Boolean(storeId && hasResolvablePackScopeQuery),
  });

  const activePackScope = hasPackScopeQuery
    ? { packs: resolvedPackScope?.packs ?? [] }
    : null;

  const packScopeKeys = useMemo(() => {
    if (!activePackScope) {
      return null;
    }

    return getCurrentInventoryPackScopeKeySet(activePackScope.packs);
  }, [activePackScope]);

  const isPackScopedView = hasPackScopeQuery;
  const isPackScopeResolving = hasPackScopeQuery && isPackScopeLoading;
  const shouldShowPackScopeMissingBanner = hasPackScopeQuery && !isPackScopeLoading && (
    isPackScopeError ||
    !activePackScope || (activePackScope.packs?.length ?? 0) === 0
  );

  const pageTitle = packScopeQueryParams
    ? buildCurrentInventoryPackScopeTitle(packScopeQueryParams)
    : 'Current Pack Inventory Status';

  return {
    packScopeQueryParams,
    activePackScope,
    packScopeKeys,
    isPackScopedView,
    isPackScopeResolving,
    shouldShowPackScopeMissingBanner,
    pageTitle,
  };
};
